import { Table } from "antd";
import { useCallback, useEffect, useState } from "react";

import { FinderTaxpayers } from "components";
import { postPartnerCertification } from "services/certifications.service";

interface Props {
  itemsPerPage?: number;
}

export function PartnerCertificatesTable(props: Props) {
  const { itemsPerPage = 10 } = props;
  const initFilters = {
    page: 1,
    page_size: itemsPerPage,
    rfc: null,
  };

  const [value, setValue] = useState("");
  const [filterTax, setFilter] = useState(initFilters);
  const [searchFilters, setSearchFilters] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [certifications, setCertifications] = useState<any[]>();

  const fetchTaxpayers = (filter: string, valueFilter: string, page?: number) => {
    setSearchFilters(filter);
    setValue(valueFilter);
    switch (filter) {
      case "rfc":
        setFilter({
          page: page,
          page_size: itemsPerPage,
          rfc: valueFilter,
        });
        break;
      default:
        setFilter({
          page: page,
          page_size: itemsPerPage,
          rfc: null,
        });
        break;
    }
  };

  function handlePagination(page) {
    fetchTaxpayers(searchFilters, value, page);
  }

  function parsePeriod(period: string) {
    const months = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];
    const [month, year] = period.split(" ");
    const monthIndex = months.findIndex((m) => m === month);
    return new Date(parseInt(year), monthIndex);
  }

  const fetchAllCertifications = useCallback(async (value: string) => {
    try {
      setIsLoading(true);
      const resp: any = await postPartnerCertification({ rfc: value });
      const sortedCertifications = resp.data.map((item)=>(
        {
          ...item,
          periodo: !item?.periodo ? "-" : item?.periodo
        }
      )).sort((a, b) => {
        const dateA = parsePeriod(a.periodo);
        const dateB = parsePeriod(b.periodo);
        return dateB.getTime() - dateA.getTime();
      });
      setCertifications(sortedCertifications);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const formatUrl = (url?: string) => {
    if (url && !url.startsWith("http")) {
      return `https://${url}`;
    }
    return url;
  };

  useEffect(() => {
    if (value) {
      fetchAllCertifications(value);
    }
  }, [value]);

  return (
    <>
      <FinderTaxpayers doSearch={fetchTaxpayers} viewSearch={false} />
      <Table
        bordered
        scroll={{ x: true }}
        loading={isLoading}
        dataSource={certifications?.map((item) => ({
          ...item,
          key: item?.id,
          month: item?.periodo,
          rfc: item?.rfc,
          name: item?.nombre,
          email: item?.correo,
          uuid: item?.uuid,
          state: item?.state ?? item?.fcstate,
          xml: item?.xml_url ?? item?.fcxmlurl,
          pdf: item?.pdf_url ?? item?.fcxmlpdf,
          penalized: item?.penalizado ? "Si" : "No",
        }))}
        pagination={{
          defaultCurrent: 1,
          pageSize: itemsPerPage,
          onChange: handlePagination,
          current: filterTax.page,
          hideOnSinglePage: true,
        }}
        columns={[
          {
            dataIndex: "month",
            title: "Periodo",
            key: "month",
          },
          {
            dataIndex: "rfc",
            title: "RFC",
            key: "rfc",
          },
          {
            dataIndex: "name",
            title: "Nombre",
            key: "name",
          },
          {
            dataIndex: "email",
            title: "Correo",
            key: "email",
          },
          {
            dataIndex: "uuid",
            title: "UUID",
            key: "uuid",
          },
          {
            dataIndex: "state",
            title: "State",
            key: "state",
          },
          {
            dataIndex: "xml",
            title: "Xml",
            key: "xml",
            render: (text, record) => (
              <a href={formatUrl(record.xml)} download={`${record.rfc}_certificado.xml`}>
                Descargar XML
              </a>
            ),
          },
          {
            dataIndex: "pdf",
            title: "Pdf",
            key: "pdf",
            render: (text, record) => (
              <a href={formatUrl(record.pdf)} download={`${record.rfc}_certificado.pdf`}>
                Descargar PDF
              </a>
            ),
          },
          {
            dataIndex: "penalized",
            title: "Penalizado",
            key: "penalized",
          },
        ]}
      />
    </>
  );
}
